import React, { useState, useEffect, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { ESTADOS_US } from "@/lib/constants";
import { hoyChicago, formatFecha } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { PageHeader, EmptyState } from "@/components/marca/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import AuthImage from "@/components/AuthImage";
import { Fuel, Download, Pencil, Loader2, AlertTriangle, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

function quarterRange(offset = 0) {
  const hoy = hoyChicago();
  const [y, m] = hoy.split("-").map(Number);
  let qIndex = Math.floor((m - 1) / 3) - offset;
  let year = y;
  while (qIndex < 0) { qIndex += 4; year -= 1; }
  const startMonth = qIndex * 3 + 1;
  const endMonth = startMonth + 2;
  const start = `${year}-${String(startMonth).padStart(2, "0")}-01`;
  const endDate = new Date(Date.UTC(year, endMonth, 0));
  const end = endDate.toISOString().slice(0, 10);
  return { start, end };
}

export default function Diesel() {
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [choferId, setChoferId] = useState("todos");
  const [camion, setCamion] = useState("");
  const [estadoUs, setEstadoUs] = useState("todos");
  const [choferes, setChoferes] = useState([]);
  const [data, setData] = useState({ items: [], total_galones: 0, total_monto: 0, por_estado: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [foto, setFoto] = useState(null);

  useEffect(() => { api.get("/choferes").then((r) => setChoferes(r.data)).catch(() => {}); }, []);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fechaDesde) params.set("fecha_desde", fechaDesde);
      if (fechaHasta) params.set("fecha_hasta", fechaHasta);
      if (choferId !== "todos") params.set("chofer_id", choferId);
      if (camion.trim()) params.set("camion", camion.trim());
      if (estadoUs !== "todos") params.set("estado_us", estadoUs);
      const { data } = await api.get(`/diesel?${params.toString()}`);
      setData(data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [fechaDesde, fechaHasta, choferId, camion, estadoUs]);

  useEffect(() => { cargar(); }, [cargar]);

  const atajo = (offset) => {
    const { start, end } = quarterRange(offset);
    setFechaDesde(start);
    setFechaHasta(end);
  };

  const exportar = async () => {
    try {
      const params = new URLSearchParams();
      if (fechaDesde) params.set("fecha_desde", fechaDesde);
      if (fechaHasta) params.set("fecha_hasta", fechaHasta);
      if (choferId !== "todos") params.set("chofer_id", choferId);
      if (camion.trim()) params.set("camion", camion.trim());
      if (estadoUs !== "todos") params.set("estado_us", estadoUs);
      const res = await api.get(`/diesel/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "recibos_diesel.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-4 lg:p-8 space-y-5">
      <PageHeader titulo={t("Diésel")} subtitulo={t("Recibos cargados por los choferes, con lectura automática por IA")} acciones={<Button data-testid="diesel-export-btn" onClick={exportar} variant="outline"><Download className="h-4 w-4" />{t(" Exportar CSV")}</Button>} />

      <div className="flex flex-wrap items-end gap-3 bg-white rounded-xl border border-line p-4">
        <div>
          <Label className="eyebrow">{t("Desde")}</Label>
          <Input data-testid="diesel-fecha-desde" type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="h-10 w-40 bg-white mt-1" />
        </div>
        <div>
          <Label className="eyebrow">{t("Hasta")}</Label>
          <Input data-testid="diesel-fecha-hasta" type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="h-10 w-40 bg-white mt-1" />
        </div>
        <div>
          <Label className="eyebrow">{t("Chofer")}</Label>
          <Select value={choferId} onValueChange={setChoferId}>
            <SelectTrigger data-testid="diesel-filtro-chofer" className="h-10 w-44 bg-white mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="todos">{t("Todos")}</SelectItem>
              {choferes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="eyebrow">{t("Camión")}</Label>
          <Input data-testid="diesel-filtro-camion" value={camion} onChange={(e) => setCamion(e.target.value)} placeholder={t("Ej: Unidad 3")} className="h-10 w-40 bg-white mt-1" />
        </div>
        <div>
          <Label className="eyebrow">{t("State")}</Label>
          <Select value={estadoUs} onValueChange={setEstadoUs}>
            <SelectTrigger data-testid="diesel-filtro-estado" className="h-10 w-28 bg-white mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white max-h-64">
              <SelectItem value="todos">{t("Todos")}</SelectItem>
              {ESTADOS_US.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button data-testid="diesel-atajo-trimestre" variant="outline" size="sm" className="h-10 rounded-full" onClick={() => atajo(0)}>{t("Este trimestre")}</Button>
          <Button data-testid="diesel-atajo-trimestre-anterior" variant="outline" size="sm" className="h-10 rounded-full" onClick={() => atajo(1)}>{t("Trimestre anterior")}</Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Totalizador testid="diesel-total-galones" label={t("Galones")} valor={data.total_galones?.toFixed(3)} />
        <Totalizador testid="diesel-total-monto" label={t("Monto")} valor={`$${data.total_monto?.toFixed(2)}`} />
        <Totalizador testid="diesel-total-recibos" label={t("Recibos")} valor={String(data.items.length)} />
      </div>

      {data.por_estado.length > 0 && (
        <div className="bg-white rounded-xl border border-line p-4 lg:p-5">
          <p className="eyebrow mb-3">{t("Por estado")}</p>
          <div className="space-y-2.5">
            {[...data.por_estado].sort((x, y) => y.galones - x.galones).map((e) => {
              const max = Math.max(...data.por_estado.map((x) => x.galones), 1);
              return (
                <div key={e.estado_us} data-testid={`diesel-subtotal-${e.estado_us}`} className="flex items-center gap-3">
                  <span data-testid={`diesel-chip-estado-${e.estado_us}`} className="h-7 w-9 rounded-md bg-[#ECE8E0] font-mono font-semibold text-[13px] flex items-center justify-center shrink-0">{e.estado_us}</span>
                  <div className="flex-1 h-2 rounded-full bg-[#ECE8E0] overflow-hidden"><span className="block h-2 rounded-full bg-ink" style={{ width: `${(e.galones / max) * 100}%` }} /></div>
                  <span className="font-mono text-[13px] text-ink-2 w-28 text-right">{t("{v0} gal", { v0: e.galones.toFixed(3) })}</span>
                  <span className="font-mono text-[13px] font-semibold text-ink w-24 text-right">${e.monto.toFixed(2)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-line overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="diesel-tabla">
            <thead>
              <tr className="bg-surface-2 text-left eyebrow">
                <th className="px-4 py-3">{t("Foto")}</th>
                <th className="px-4 py-3">{t("Fecha")}</th>
                <th className="px-4 py-3">{t("Chofer")}</th>
                <th className="px-4 py-3">{t("Camión")}</th>
                <th className="px-4 py-3">{t("State")}</th>
                <th className="px-4 py-3">{t("Estación / Ciudad")}</th>
                <th className="px-4 py-3">{t("Galones")}</th>
                <th className="px-4 py-3">{t("$/Galón")}</th>
                <th className="px-4 py-3">{t("Monto")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={10}><EmptyState icon={Fuel} texto={t("No hay recibos de diésel en este período")} /></td></tr>
              )}
              {data.items.map((r) => (
                <tr key={r.id} data-testid={`diesel-fila-${r.id}`} className="hover:bg-surface-2">
                  <td className="px-4 py-2">
                    <button onClick={() => setFoto(r.foto_file_id)} className="h-10 w-10 rounded-md overflow-hidden border block">
                      <AuthImage fileId={r.foto_file_id} className="h-full w-full object-cover" />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-ink-2">{formatFecha(r.fecha)}</td>
                  <td className="px-4 py-3 text-ink-2">{r.chofer_nombre}</td>
                  <td className="px-4 py-3 text-ink-2">{r.camion || "—"}</td>
                  <td className="px-4 py-3"><span className="inline-flex h-7 w-9 rounded-md bg-[#ECE8E0] font-mono font-semibold text-[13px] items-center justify-center">{r.estado_us}</span></td>
                  <td className="px-4 py-3 text-ink-2">{r.estacion || "—"}{r.ciudad ? `, ${r.ciudad}` : ""}</td>
                  <td className="px-4 py-3 font-mono text-ink-2">{r.galones.toFixed(3)}</td>
                  <td className="px-4 py-3 font-mono text-ink-2">{r.precio_galon ? `$${r.precio_galon.toFixed(2)}` : "—"}</td>
                  <td className="px-4 py-3 font-mono font-semibold text-ink">${r.monto.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">
                    {r.corregido_por_chofer && <span title={t("Corregido por el chofer")} className="inline-flex items-center gap-1 text-[12px] text-[#B07A12] mr-2"><AlertTriangle className="h-3.5 w-3.5" />{t(" corregido")}</span>}
                    <Button data-testid={`diesel-editar-${r.id}`} variant="ghost" size="sm" onClick={() => setEditing(r)}><Pencil className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {foto && (
        <Dialog open onOpenChange={(v) => !v && setFoto(null)}>
          <DialogContent className="bg-white max-w-2xl">
            <DialogHeader><DialogTitle>{t("Foto del recibo")}</DialogTitle></DialogHeader>
            <AuthImage fileId={foto} className="w-full max-h-[70vh] object-contain rounded-lg border" />
          </DialogContent>
        </Dialog>
      )}

      {editing && <EditarDieselDialog recibo={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); cargar(); }} />}
    </div>
  );
}

function Totalizador({ label, valor, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-xl border p-4">
      <p className="eyebrow">{label}</p>
      <p className="font-display font-semibold text-[28px] leading-none text-ink mt-2 tabular">{valor}</p>
    </div>
  );
}

function EditarDieselDialog({ recibo, onClose, onSaved }) {
  const [fecha, setFecha] = useState(recibo.fecha);
  const [monto, setMonto] = useState(String(recibo.monto));
  const [galones, setGalones] = useState(String(recibo.galones));
  const [precioGalon, setPrecioGalon] = useState(recibo.precio_galon != null ? String(recibo.precio_galon) : "");
  const [estadoUs, setEstadoUs] = useState(recibo.estado_us);
  const [estacion, setEstacion] = useState(recibo.estacion || "");
  const [ciudad, setCiudad] = useState(recibo.ciudad || "");
  const [saving, setSaving] = useState(false);

  const guardar = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/diesel/${recibo.id}`, {
        fecha, monto: parseFloat(monto), galones: parseFloat(galones),
        precio_galon: precioGalon ? parseFloat(precioGalon) : null,
        estado_us: estadoUs, estacion, ciudad,
      });
      toast.success(t("Recibo corregido"));
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("Corregir recibo")}</DialogTitle>
          <DialogDescription>{t("Edita los campos que estén mal leídos y guarda para registrar la corrección.")}</DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-4">
          <AuthImage fileId={recibo.foto_file_id} className="w-full h-56 object-contain rounded-lg border bg-surface-2" />
          <form onSubmit={guardar} className="space-y-3">
            <div><Label className="eyebrow">{t("Fecha")}</Label><Input data-testid="diesel-edit-fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="mt-1 h-10" required /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="eyebrow">{t("Monto")}</Label><Input data-testid="diesel-edit-monto" type="number" step="0.01" value={monto} onChange={(e) => setMonto(e.target.value)} className="mt-1 h-10" required /></div>
              <div><Label className="eyebrow">{t("Galones")}</Label><Input data-testid="diesel-edit-galones" type="number" step="0.001" value={galones} onChange={(e) => setGalones(e.target.value)} className="mt-1 h-10" required /></div>
            </div>
            <div><Label className="eyebrow">{t("Precio/Galón")}</Label><Input data-testid="diesel-edit-precio" type="number" step="0.01" value={precioGalon} onChange={(e) => setPrecioGalon(e.target.value)} className="mt-1 h-10" /></div>
            <div>
              <Label className="eyebrow">{t("State")}</Label>
              <Select value={estadoUs} onValueChange={setEstadoUs}>
                <SelectTrigger data-testid="diesel-edit-estado" className="h-10 bg-white mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  {ESTADOS_US.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="eyebrow">{t("Estación")}</Label><Input value={estacion} onChange={(e) => setEstacion(e.target.value)} className="mt-1 h-10" /></div>
              <div><Label className="eyebrow">{t("Ciudad")}</Label><Input value={ciudad} onChange={(e) => setCiudad(e.target.value)} className="mt-1 h-10" /></div>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={onClose}>{t("Cancelar")}</Button>
              <Button type="submit" data-testid="diesel-edit-guardar" className="bg-brand hover:bg-brand-dark text-white" disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Guardar")}</Button>
            </DialogFooter>
          </form>
        </div>
        {recibo.correcciones?.length > 0 && (
          <div className="mt-2 border-t pt-3">
            <p className="eyebrow mb-2">{t("Historial de correcciones")}</p>
            <div className="space-y-1 max-h-24 overflow-y-auto">
              {recibo.correcciones.map((c, i) => (
                <p key={i} className="text-xs text-ink-3">{c.por} — {Object.keys(c.cambios).join(", ")}</p>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
